import type { ReactNode } from 'react';
import { Color, Size, t } from '@bms2/shared';
import { Button } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { PageModule } from '../../shell/PageModule';
import styles from './DocumentEditPage.module.css';

/**
 * 🔒 An accounting document's edit mode (decision 0409): the document's own
 * header fields first, then the lines on the Item Table, and the shell's own
 * footer on the floor carrying Save as Draft, the document's first outward move,
 * and Cancel (T113).
 *
 * 🔨 The second control is that document's own first outward move, never the
 * word Send everywhere: a Quote and a Purchase Order send, a Work Order
 * schedules. Save and Send saves first and opens the send after, so a send
 * that fails leaves the document saved rather than nothing.
 *
 * 🔨 A document with no draft state draws no Save as Draft. A Credit Note is
 * issued the moment it is raised, so the control would save nothing a person
 * could come back to, and two controls doing one act is worse than one.
 */
export interface DocumentEditPageProps {
  /** What the banner reads: New Purchase Order on a create, the number on an edit. */
  title: string;
  subtitle: string;
  /** The header fields, in the order the document's own piece fixes. */
  header: ReactNode;
  /** The lines, on the Item Table over that document's column set. */
  lines: ReactNode;
  /** The second footer control: Send on a Quote or a Purchase Order, Schedule on a Work Order. */
  outwardLabel: string;
  busy?: boolean;
  error?: string | null;
  onDismissError?: () => void;
  /** Overrides the error banner's action, which otherwise dismisses; T295's Work Order form links a missing priority tier to its editor. */
  errorAction?: { label: string; onAction: () => void } | null;
  /** Absent where the document has no draft to save, and then the outward move is the footer's only act. */
  onSaveDraft?: (() => void) | null;
  onSaveAndSend: () => void;
  onCancel: () => void;
  /** 🔒 Decision 5570: a section under the lines, the new Work Order's first visit; absent draws nothing. */
  after?: { heading: string; body: ReactNode } | null;
}

export function DocumentEditPage({ title, subtitle, header, lines, outwardLabel, busy = false, error = null, onDismissError, errorAction = null, onSaveDraft = null, onSaveAndSend, onCancel, after = null }: DocumentEditPageProps) {
  /**
   * 🔒 The form's footer is the shell's footer slot now (T113), on the floor of the
   * frame with the form scrolling above it. 💰 It was a sticky block inside the
   * content region, because the slot was hidden on a page with no status pill and an
   * edit form has no status; the slot is shown on footer content now, so the
   * exception is gone rather than explained.
   */
  const footer = (
    <div className={styles.footer} data-document-footer={onSaveDraft ? 'edit' : 'edit-no-draft'}>
      {onSaveDraft ? <Button size={Size.Medium} color={Color.Navy} text={[t('document.edit.saveDraft')]} actions={[onSaveDraft]} disabled={busy} /> : null}
      <Button size={Size.Medium} color={Color.Navy} text={[outwardLabel]} actions={[onSaveAndSend]} disabled={busy} />
      <span className={styles.footerSpacer} />
      <Button size={Size.Medium} color={Color.Inactive} text={[t('modal.cancel')]} actions={[onCancel]} disabled={busy} />
    </div>
  );
  return (
    <PageModule icon="accounting" title={title} subtitle={subtitle} footer={footer}>
      <div className={styles.form} data-document-mode="edit">
        {error ? <AlertBanner tone="error" message={error} action={errorAction ?? { label: t('modal.dismiss'), onAction: onDismissError ?? (() => undefined) }} /> : null}
        <section className={styles.section} data-document-header="true">
          <h2 className={styles.heading}>{t('document.edit.header')}</h2>
          <div className={styles.fields}>{header}</div>
        </section>
        <section className={styles.section} data-document-lines="true">
          <h2 className={styles.heading}>{t('document.edit.lines')}</h2>
          {lines}
        </section>
        {after ? (
          <section className={styles.section} data-document-after="true">
            <h2 className={styles.heading}>{after.heading}</h2>
            <div className={styles.fields}>{after.body}</div>
          </section>
        ) : null}
      </div>
    </PageModule>
  );
}

/** The class names the concrete forms share, so a document's header fields lay out the same way. */
export const documentEditStyles = styles;
